import React, { useState, useMemo, useCallback } from 'react';
import {
  Card,
  Typography,
  Space,
  Select,
  DatePicker,
  Input,
  Tag,
  Alert,
  Tooltip,
  theme,
  Row,
  Col,
  Statistic,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { ApplicationCell, DataTable, ExecutionCell, MoneyCell } from '../../ui';
import {
  RouteApplicationsCompactText,
  RouteClientsCompactText
} from '../../features/routes/RouteMultiAppCompactCells';
import dayjs from 'dayjs';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  useGetRoutesExtendedQuery,
  useGetApplicationsExtendedQuery,
  useGetObligationsReadQuery,
  type ApplicationExtendedItem,
} from '../../store/api/financeApi';
import { useGetCarriersQuery, useGetClientsQuery } from '../../store/api/referencesApi';
import { useGetUsersQuery } from '../../store/api/usersApi';
import {
  filterResponsibleEligibleUsers,
  formatResponsibleUserLabel,
} from '../../utils/userDisplayName';
import { formatRouteDirection } from '../../utils/addressUtils';
import FinanceDetailsDrawer from './FinanceDetailsDrawer';
import { Route, TransportType, ApplicationStatus } from '../../types';
import { navigateToReferenceCard } from '../../utils/referenceCardNavigation';
import { formatCurrency } from '../../utils/dateUtils';
import GovernanceRiskHeader from '../../components/governance/GovernanceRiskHeader';
import { aggregateFinanceWorkspaceProfitKpi } from '../../features/finance/domain/financeWorkspaceKpi';

/** Высота тела таблицы внутри карточки — две секции на одном экране, без бесконечной страницы */
const WORKSPACE_TABLE_SCROLL_Y = 'clamp(200px, 28vh, 360px)';

const { Text } = Typography;
const { RangePicker } = DatePicker;

/** Статусы оплаты по обязательствам (единая подпись в UI) */
const OBLIGATION_PAYMENT_LABEL: Record<string, string> = {
  NOT_PAID: 'Не оплачено',
  PARTIALLY_PAID: 'Частично',
  PAID: 'Оплачено',
  OVERDUE: 'Просрочено',
};

function labelForObligationPaymentStatus(code: string): string {
  const u = String(code || '').toUpperCase();
  return (OBLIGATION_PAYMENT_LABEL[u] ?? code) || '—';
}

interface ExtendedRoute extends Route {
  payable?: {
    totalRequired: number;
    totalPaid: number;
    remaining: number;
    overdue: number;
    maxDaysOverdue: number;
    nearestDueDate?: string | Date | null;
  };
  outgoing?: {
    total: number;
    paid: number;
    remaining: number;
    nearestDueDate?: string | null;
    daysLeft?: number | null;
  };
  controlIssues?: {
    critical: { code: string; message: string }[];
    warnings: { code: string; message: string }[];
    info: { code: string; message: string }[];
    totalCount: number;
    hasCritical: boolean;
  };
  aggregatedStatus?: { overallStatus: 'OPEN' | 'PARTIAL' | 'OK' | 'OVERDUE' };
}

function isOwnTransportExecutor(record: ExtendedRoute): boolean {
  const exec = String(record.executor || '').toUpperCase();
  return exec === 'COMPANY' || exec === 'OUR_COMPANY';
}

/** Статус строки маршрута только по кредиторке (EXPENSE / payable), без дебиторки */
function getRoutePayableDisplayStatus(record: ExtendedRoute): string {
  if (isOwnTransportExecutor(record)) return '—';
  const p = record.payable;
  if (!p || (Number(p.totalRequired ?? 0) <= 0 && Number(p.remaining ?? 0) <= 0.005)) return '—';
  if ((p.remaining ?? 0) <= 0.005 && (p.totalRequired ?? 0) > 0) return 'PAID';

  const out = record.outgoing;
  if (out && (out.remaining ?? 0) > 0 && out.daysLeft != null && out.daysLeft < 0) {
    return 'OVERDUE';
  }
  if ((p?.overdue ?? 0) > 0.005 && (p?.remaining ?? 0) > 0.005) {
    return 'OVERDUE';
  }
  if ((p?.totalPaid ?? 0) > 0.005 && (p?.remaining ?? 0) > 0.005) {
    return 'PARTIALLY_PAID';
  }
  return 'NOT_PAID';
}

function formatPayableDueDate(record: ExtendedRoute): string {
  if (isOwnTransportExecutor(record)) return '—';
  const raw = record.payable?.nearestDueDate ?? record.outgoing?.nearestDueDate;
  if (raw == null || raw === '') return '—';
  const d = dayjs(raw);
  return d.isValid() ? d.format('DD.MM.YYYY') : '—';
}

function getRoutesExpenseRowClassName(record: ExtendedRoute): string {
  if (isOwnTransportExecutor(record)) return '';
  const out = record.outgoing;
  if (out && out.remaining > 0 && out.daysLeft != null && out.daysLeft < 0) return 'row-overdue';
  if ((record.payable?.overdue ?? 0) > 0.005 && (record.payable?.remaining ?? 0) > 0.005) return 'row-overdue';
  if (out && out.remaining > 0 && out.daysLeft != null && out.daysLeft >= 0 && out.daysLeft <= 3) return 'row-warning';
  const due = record.payable?.nearestDueDate ?? record.outgoing?.nearestDueDate;
  if (due && (record.payable?.remaining ?? 0) > 0.005) {
    const diff = dayjs(due).startOf('day').diff(dayjs().startOf('day'), 'day');
    if (diff >= 0 && diff <= 3) return 'row-warning';
  }
  return '';
}

function controlIssuesTooltip(record: ExtendedRoute): React.ReactNode {
  const ci = record.controlIssues;
  if (!ci || (ci.totalCount ?? 0) === 0) return null;
  const lines = [
    ...(ci.critical || []).map((x) => `[${x.code}] ${x.message}`),
    ...(ci.warnings || []).map((x) => `[${x.code}] ${x.message}`),
    ...(ci.info || []).map((x) => `[${x.code}] ${x.message}`),
  ];
  return lines.length ? lines.join('\n') : null;
}

function hasCarrierInvoiceWarning(record: ExtendedRoute): boolean {
  const ci = record.controlIssues;
  if (!ci) return false;
  const all = [...(ci.critical || []), ...(ci.warnings || []), ...(ci.info || [])];
  return all.some((x) => x.code === 'CARRIER_INVOICE_MISSING');
}

function formatAppDirection(app: Record<string, unknown>): string {
  const lc = app?.loadingCity ?? app?.loadingAddress;
  const uc = app?.unloadingCity ?? app?.unloadingAddress;
  const a = lc != null ? String(lc) : '';
  const b = uc != null ? String(uc) : '';
  if (!a && !b) return '—';
  const s = formatRouteDirection(a, b);
  return s.length > 64 ? `${s.slice(0, 64)}…` : s;
}

function formatFactDate(raw: string | null | undefined): string {
  if (!raw) return '—';
  const d = dayjs(raw);
  return d.isValid() ? d.format('DD.MM.YYYY') : '—';
}

function formatClientRequestLine(app: Record<string, unknown>): string {
  const ext = app?.externalNumber != null ? String(app.externalNumber).trim() : '';
  const basisDate = app?.basisDate != null ? String(app.basisDate) : '';
  const appDate = app?.applicationDate != null ? String(app.applicationDate) : '';
  const dateStr = basisDate || appDate;
  if (!ext) return '—';
  if (!dateStr) return ext;
  const d = dayjs(dateStr);
  return `${ext} · ${d.isValid() ? d.format('DD.MM.YYYY') : dateStr}`;
}

/** Остаток дебиторки в workspace: из read-model (Σ remaining по обязательствам), иначе total − paid. */
function applicationExtendedIncomeRemaining(row: ApplicationExtendedItem): number {
  const apiRemaining = Number(row.income?.remaining);
  if (Number.isFinite(apiRemaining)) {
    return Math.max(0, Math.round(apiRemaining * 100) / 100);
  }
  const total = Number(row.income?.total ?? 0);
  const paid = Number(row.income?.paid ?? 0);
  return Math.max(0, Math.round((total - paid) * 100) / 100);
}

function getApplicationDisplayStatus(
  record: ApplicationExtendedItem,
  nearestDueIso: string | null | undefined
): string {
  const obligation = Number(record.income?.total ?? 0);
  const received = Number(record.income?.paid ?? 0);
  const remaining = applicationExtendedIncomeRemaining(record);
  const today = dayjs().startOf('day');

  if (nearestDueIso && remaining > 0.005) {
    const d = dayjs(nearestDueIso).startOf('day');
    if (d.isValid() && d.isBefore(today)) {
      return 'OVERDUE';
    }
  }

  if (received <= 0.005) return 'NOT_PAID';
  if (received < obligation - 0.005) return 'PARTIALLY_PAID';
  return 'PAID';
}

const FinanceWorkspacePage: React.FC = () => {
  const { token } = theme.useToken();
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [status, setStatus] = useState<string | undefined>();
  const [transportType, setTransportType] = useState<string | undefined>();
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null]>([null, null]);
  const [search, setSearch] = useState('');
  const [carrierId, setCarrierId] = useState<number | undefined>();
  const [responsibleLogistId, setResponsibleLogistId] = useState<number | undefined>();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<'route' | 'application' | null>(null);
  const [selectedRoute, setSelectedRoute] = useState<ExtendedRoute | null>(null);
  const [selectedApplicationId, setSelectedApplicationId] = useState<number | null>(null);

  const [appsPage, setAppsPage] = useState(1);
  const [appsLimit] = useState(20);
  const [appsSearch, setAppsSearch] = useState('');
  const [appsWorkflowStatus, setAppsWorkflowStatus] = useState<string | undefined>();
  const [appsClientId, setAppsClientId] = useState<number | undefined>();
  const [appsDirection, setAppsDirection] = useState('');
  const [appsResponsibleLogistId, setAppsResponsibleLogistId] = useState<number | undefined>();
  const [appsDateRange, setAppsDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null]>([null, null]);

  const navigate = useNavigate();
  const location = useLocation();

  const openRouteInDrawer = useCallback((record: ExtendedRoute) => {
    setSelectedRoute(record);
    setDrawerMode('route');
    setSelectedApplicationId(null);
    setDrawerOpen(true);
  }, []);

  const openApplicationInDrawer = useCallback((record: ApplicationExtendedItem) => {
    setSelectedApplicationId(record.application.id);
    setDrawerMode('application');
    setSelectedRoute(null);
    setDrawerOpen(true);
  }, []);

  const { data, isLoading, refetch } = useGetRoutesExtendedQuery({
    page,
    limit,
    status: status || undefined,
    transportType: transportType || undefined,
    dateFrom: dateRange[0] ? dateRange[0].format('YYYY-MM-DD') : undefined,
    dateTo: dateRange[1] ? dateRange[1].format('YYYY-MM-DD') : undefined,
    search: search || undefined,
    carrierId,
    responsibleLogistId,
  });

  const {
    data: appsData,
    isLoading: appsLoading,
    isError: appsQueryError,
    error: appsError,
    refetch: refetchApps,
  } = useGetApplicationsExtendedQuery({
    page: appsPage,
    limit: appsLimit,
    search: appsSearch || undefined,
    status: appsWorkflowStatus,
    clientId: appsClientId,
    responsibleLogistId: appsResponsibleLogistId,
    dateFrom: appsDateRange[0] ? appsDateRange[0].format('YYYY-MM-DD') : undefined,
    dateTo: appsDateRange[1] ? appsDateRange[1].format('YYYY-MM-DD') : undefined,
    direction: appsDirection || undefined,
  });

  /** Ближайший срок по INCOME-обязательствам (тот же read-model, без изменений API) */
  const { data: incomeOblForDue } = useGetObligationsReadQuery({
    page: 1,
    limit: 500,
    type: 'INCOMING',
  });

  const applicationNearestIncomeDue = useMemo(() => {
    const map = new Map<number, string>();
    for (const o of incomeOblForDue?.obligations ?? []) {
      if ((o.remainingAmount ?? 0) <= 0.005) continue;
      if (!o.dueDate) continue;
      const aid = o.applicationId;
      if (aid == null) continue;
      const prev = map.get(aid);
      if (!prev || new Date(o.dueDate).getTime() < new Date(prev).getTime()) {
        map.set(aid, o.dueDate);
      }
    }
    return map;
  }, [incomeOblForDue]);

  const { data: carriers = [] } = useGetCarriersQuery();
  const { data: clients = [] } = useGetClientsQuery();
  const { data: users = [] } = useGetUsersQuery();

  const routes = useMemo(() => data?.routes ?? [], [data?.routes]);
  const pagination = data?.pagination ?? { page: 1, limit: 20, total: 0, pages: 0 };

  const applicationsExtended = useMemo(
    () => appsData?.applicationsExtended ?? [],
    [appsData?.applicationsExtended]
  );
  const appsPagination = appsData?.pagination ?? { page: 1, limit: 20, total: 0, pages: 0 };

  const workspaceKpi = useMemo(() => {
    let receivable = 0;
    for (const app of applicationsExtended) {
      receivable += applicationExtendedIncomeRemaining(app);
    }
    let payable = 0;
    for (const r of routes as ExtendedRoute[]) {
      if (isOwnTransportExecutor(r)) continue;
      payable += Number(r.payable?.remaining ?? 0);
    }
    const { profitFact, profitPlan } = aggregateFinanceWorkspaceProfitKpi(applicationsExtended);
    return { receivable, payable, profitFact, profitPlan };
  }, [applicationsExtended, routes]);

  const routesColumns: ColumnsType<ExtendedRoute> = useMemo(
    () => [
      {
        title: 'Контроль',
        key: 'controlIssues',
        width: 88,
        render: (_, record) => {
          const ci = record.controlIssues;
          const tip = controlIssuesTooltip(record);
          if (!ci || (ci.totalCount ?? 0) === 0) {
            return <Text type="secondary">—</Text>;
          }
          const color = ci.hasCritical ? 'red' : ci.warnings?.length ? 'orange' : 'blue';
          const tag = (
            <Tag color={color} icon={<ExclamationCircleOutlined />}>
              {ci.totalCount}
            </Tag>
          );
          return tip ? <Tooltip title={<pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{tip}</pre>}>{tag}</Tooltip> : tag;
        },
      },
      {
        title: '№ маршрута',
        key: 'routeId',
        width: 110,
        render: (_, record) => (
          <Text
            strong
            style={{ color: token.colorPrimary, cursor: 'pointer' }}
            onClick={(e) => {
              e.stopPropagation();
              openRouteInDrawer(record);
            }}
          >
            #{record.id}
          </Text>
        ),
      },
      {
        title: 'Направление',
        key: 'direction',
        width: 200,
        render: (_, record) => {
          const dir = formatRouteDirection(record.loadingAddress || '', record.unloadingAddress || '');
          return (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {dir.length > 64 ? `${dir.slice(0, 64)}…` : dir}
            </Text>
          );
        },
      },
      {
        title: 'Перевозчик',
        key: 'carrier',
        width: 150,
        render: (_, record) => {
          const id = record.carrier?.id;
          const name = record.carrier?.name || '—';
          if (!id) return <Text style={{ fontSize: 12 }}>{name}</Text>;
          return (
            <Text
              style={{ fontSize: 12, color: token.colorPrimary, cursor: 'pointer' }}
              onClick={(e) => {
                e.stopPropagation();
                navigateToReferenceCard(navigate, `/carriers/${encodeURIComponent(String(id))}`, location);
              }}
            >
              {name}
            </Text>
          );
        },
      },
      {
        title: 'Заявка',
        key: 'application',
        width: 130,
        render: (_, record) => (
          <RouteApplicationsCompactText
            applications={record.applications as any}
            singleAppUseApplicationCell
          />
        ),
      },
      {
        title: 'Клиент',
        key: 'client',
        width: 160,
        render: (_, record) => (
          <RouteClientsCompactText
            applications={record.applications as any}
            fallbackClient={record.client ?? null}
            style={{ fontSize: 12 }}
          />
        ),
      },
      {
        title: 'Логист',
        key: 'responsibleLogist',
        width: 160,
        ellipsis: true,
        render: (_, record) => (
          <Text style={{ fontSize: 12 }}>{formatResponsibleUserLabel(record.responsibleLogist)}</Text>
        ),
      },
      {
        title: 'Факт погрузки',
        key: 'actualLoadingDate',
        width: 118,
        render: (_, record) => (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {formatFactDate(record.actualLoadingDate as string | null | undefined)}
          </Text>
        ),
      },
      {
        title: 'Факт выгрузки',
        key: 'actualUnloadingDate',
        width: 118,
        render: (_, record) => (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {formatFactDate(record.actualUnloadingDate as string | null | undefined)}
          </Text>
        ),
      },
      {
        title: 'Договор-заявка',
        key: 'carrierContract',
        width: 160,
        render: (_, record) => (
          <div onClick={(e) => e.stopPropagation()}>
            <ExecutionCell
              route={{
                isCompanyRoute: record.isCompanyRoute,
                carrierContractRequestNumber: record.carrierContractRequestNumber,
                carrierContractRequestDate: record.carrierContractRequestDate,
                carrierAgreement: record.carrierAgreement,
              }}
            />
          </div>
        ),
      },
      {
        title: 'Оплачено',
        key: 'paid',
        width: 120,
        align: 'right',
        render: (_, record) => {
          if (isOwnTransportExecutor(record)) return <Text type="secondary">—</Text>;
          const v = Number(record.payable?.totalPaid ?? 0);
          return <MoneyCell value={v} type="expense" />;
        },
      },
      {
        title: 'К оплате',
        key: 'remaining',
        width: 120,
        align: 'right',
        render: (_, record) => {
          if (isOwnTransportExecutor(record)) return <Text type="secondary">—</Text>;
          const v = Number(record.payable?.remaining ?? 0);
          return <MoneyCell value={v} type="expense" />;
        },
      },
      {
        title: 'Срок оплаты',
        key: 'due',
        width: 118,
        render: (_, record) => <Text type="secondary">{formatPayableDueDate(record)}</Text>,
      },
      {
        title: 'Статус',
        key: 'payableStatus',
        width: 130,
        render: (_, record) => {
          const code = getRoutePayableDisplayStatus(record);
          if (code === '—') return <Text type="secondary">—</Text>;
          return <Text>{labelForObligationPaymentStatus(code)}</Text>;
        },
      },
    ],
    [navigate, location, token.colorPrimary, openRouteInDrawer]
  );

  const applicationsColumns: ColumnsType<ApplicationExtendedItem> = useMemo(
    () => [
      {
        title: 'Заявка',
        key: 'internalNumber',
        width: 130,
        render: (_, record) => {
          const a = record.application as { id: number; internalNumber?: string | null; applicationDate?: string | null };
          return <ApplicationCell application={a} />;
        },
      },
      {
        title: 'Направление',
        key: 'direction',
        width: 200,
        render: (_, record) => (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {formatAppDirection(record.application as Record<string, unknown>)}
          </Text>
        ),
      },
      {
        title: 'Клиент',
        key: 'client',
        width: 170,
        ellipsis: true,
        render: (_, record) => {
          const c = record.client;
          if (!c) return '—';
          return (
            <Text
              style={{ color: token.colorPrimary, cursor: 'pointer' }}
              onClick={(e) => {
                e.stopPropagation();
                navigateToReferenceCard(navigate, `/contractors/${encodeURIComponent(String(c.id))}`, location);
              }}
            >
              {c.name}
            </Text>
          );
        },
      },
      {
        title: 'Логист',
        key: 'responsible',
        width: 160,
        ellipsis: true,
        render: (_, record) => (
          <Text style={{ fontSize: 12 }}>{formatResponsibleUserLabel(record.responsible)}</Text>
        ),
      },
      {
        title: 'Факт погрузки',
        key: 'actualLoadingDate',
        width: 118,
        render: (_, record) => (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {formatFactDate(record.actualLoadingDate)}
          </Text>
        ),
      },
      {
        title: 'Факт выгрузки',
        key: 'actualUnloadingDate',
        width: 118,
        render: (_, record) => (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {formatFactDate(record.actualUnloadingDate)}
          </Text>
        ),
      },
      {
        title: 'Заявка клиента',
        key: 'clientRequest',
        width: 180,
        ellipsis: true,
        render: (_, record) => (
          <Text style={{ fontSize: 12 }}>{formatClientRequestLine(record.application as Record<string, unknown>)}</Text>
        ),
      },
      {
        title: 'Получено',
        key: 'incomePaid',
        align: 'right',
        width: 120,
        render: (_, record) => <MoneyCell value={record.income.paid} type="income" />,
      },
      {
        title: 'Ожидается',
        key: 'incomeRemaining',
        align: 'right',
        width: 120,
        render: (_, record) => (
          <MoneyCell value={applicationExtendedIncomeRemaining(record)} type="income" />
        ),
      },
      {
        title: 'Срок оплаты',
        key: 'due',
        width: 118,
        render: (_, record) => {
          const raw = applicationNearestIncomeDue.get(record.application.id);
          if (!raw) return <Text type="secondary">—</Text>;
          const d = dayjs(raw);
          return <Text type="secondary">{d.isValid() ? d.format('DD.MM.YYYY') : '—'}</Text>;
        },
      },
      {
        title: 'Статус',
        key: 'finStatus',
        width: 130,
        render: (_, record) => {
          const due = applicationNearestIncomeDue.get(record.application.id);
          const code = getApplicationDisplayStatus(record, due);
          return <Text>{labelForObligationPaymentStatus(code)}</Text>;
        },
      },
    ],
    [applicationNearestIncomeDue, navigate, location, token.colorPrimary]
  );

  const handleDrawerClose = () => {
    void refetch();
    void refetchApps();
    setDrawerOpen(false);
    setDrawerMode(null);
    setSelectedRoute(null);
    setSelectedApplicationId(null);
  };

  const routesSection = (
    <>
      <Space wrap style={{ marginBottom: 16 }} size="middle">
        <Select
          placeholder="Статус"
          allowClear
          style={{ width: 140 }}
          value={status || undefined}
          onChange={setStatus}
          options={[
            { value: 'created', label: 'Создан' },
            { value: 'in_transit', label: 'В пути' },
            { value: 'completed', label: 'Завершен' },
            { value: 'cancelled', label: 'Отменен' },
          ]}
        />
        <Select
          placeholder="Тип транспорта"
          allowClear
          style={{ width: 140 }}
          value={transportType || undefined}
          onChange={setTransportType}
          options={[
            { value: TransportType.AUTO, label: 'Авто' },
            { value: TransportType.RAILWAY, label: 'Ж/д' },
            { value: TransportType.AVIATION, label: 'Авиа' },
          ]}
        />
        <RangePicker
          value={dateRange}
          onChange={(dates) => setDateRange(dates as [dayjs.Dayjs | null, dayjs.Dayjs | null])}
        />
        <Select
          placeholder="Перевозчик"
          allowClear
          showSearch
          optionFilterProp="label"
          style={{ width: 200 }}
          value={carrierId}
          onChange={setCarrierId}
          options={carriers.map((c) => ({ value: c.id, label: c.name }))}
        />
        <Select
          placeholder="Логист"
          allowClear
          showSearch
          optionFilterProp="label"
          style={{ width: 180 }}
          value={responsibleLogistId}
          onChange={setResponsibleLogistId}
          options={filterResponsibleEligibleUsers(users).map((u) => ({
            value: u.id,
            label: formatResponsibleUserLabel(u),
          }))}
        />
        <Input.Search
          placeholder="Поиск..."
          allowClear
          style={{ width: 200 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onSearch={() => {}}
        />
      </Space>

      {routes.some((r) => !isOwnTransportExecutor(r as ExtendedRoute) && hasCarrierInvoiceWarning(r as ExtendedRoute)) && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="По части маршрутов нет счёта перевозчика (см. контроль). Оформите документы перед оплатой."
        />
      )}

      <DataTable<ExtendedRoute>
        rowKey="id"
        columns={routesColumns}
        dataSource={routes as ExtendedRoute[]}
        rowClassName={(record) => getRoutesExpenseRowClassName(record)}
        loading={isLoading}
        pagination={{
          current: pagination.page,
          pageSize: pagination.limit,
          total: pagination.total,
          showSizeChanger: false,
          showTotal: (total) => `Всего: ${total}`,
          onChange: setPage,
        }}
        onRow={(record) => ({
          onClick: () => openRouteInDrawer(record),
          style: { cursor: 'pointer' },
        })}
        scroll={{ x: 'max-content', y: WORKSPACE_TABLE_SCROLL_Y }}
      />
    </>
  );

  const applicationsSection = (
    <>
      <Space wrap style={{ marginBottom: 16 }} size="middle">
        <Select
          placeholder="Статус заявки"
          allowClear
          style={{ width: 180 }}
          value={appsWorkflowStatus}
          onChange={(v) => {
            setAppsWorkflowStatus(v);
            setAppsPage(1);
          }}
          options={[
            { value: ApplicationStatus.CREATED, label: 'Создана' },
            { value: ApplicationStatus.IN_WORK, label: 'В работе' },
            { value: ApplicationStatus.COMPLETED, label: 'Завершена' },
            { value: ApplicationStatus.CANCELLED, label: 'Отменена' },
          ]}
        />
        <Input.Search
          placeholder="Номер заявки..."
          allowClear
          style={{ width: 240 }}
          value={appsSearch}
          onChange={(e) => {
            setAppsSearch(e.target.value);
            setAppsPage(1);
          }}
          onSearch={() => setAppsPage(1)}
        />
        <Select
          placeholder="Клиент"
          allowClear
          showSearch
          optionFilterProp="label"
          style={{ width: 200 }}
          value={appsClientId}
          onChange={(v) => {
            setAppsClientId(v);
            setAppsPage(1);
          }}
          options={clients.map((c) => ({ value: c.id, label: c.name }))}
        />
        <Input.Search
          placeholder="Направление..."
          allowClear
          style={{ width: 200 }}
          value={appsDirection}
          onChange={(e) => {
            setAppsDirection(e.target.value);
            setAppsPage(1);
          }}
          onSearch={() => setAppsPage(1)}
        />
        <Select
          placeholder="Ответственный"
          allowClear
          showSearch
          optionFilterProp="label"
          style={{ width: 200 }}
          value={appsResponsibleLogistId}
          onChange={(v) => {
            setAppsResponsibleLogistId(v);
            setAppsPage(1);
          }}
          options={filterResponsibleEligibleUsers(users).map((u) => ({
            value: u.id,
            label: formatResponsibleUserLabel(u),
          }))}
        />
        <RangePicker
          value={appsDateRange}
          onChange={(dates) => {
            setAppsDateRange(dates as [dayjs.Dayjs | null, dayjs.Dayjs | null]);
            setAppsPage(1);
          }}
        />
      </Space>

      {appsQueryError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="Не удалось загрузить заявки"
          description={
            (() => {
              const ed = appsError as {
                data?: { error?: string; path?: string };
                status?: string | number;
              };
              const parts = [
                ed?.data?.error,
                ed?.data?.path ? `Запрошенный путь: ${ed.data.path}` : null,
                ed?.status != null ? `HTTP ${ed.status}` : null,
              ].filter(Boolean);
              return parts.length ? parts.join(' — ') : 'Проверьте сеть и URL API (должен быть /api/... на бэкенде).';
            })()
          }
        />
      )}

      <DataTable<ApplicationExtendedItem>
        rowKey={(r) => String(r.application.id)}
        columns={applicationsColumns}
        dataSource={applicationsExtended}
        loading={appsLoading}
        pagination={{
          current: appsPagination.page,
          pageSize: appsPagination.limit,
          total: appsPagination.total,
          showSizeChanger: false,
          showTotal: (total) => `Всего: ${total}`,
          onChange: setAppsPage,
        }}
        onRow={(record) => ({
          onClick: () => openApplicationInDrawer(record),
          style: { cursor: 'pointer' },
        })}
        scroll={{ x: 'max-content', y: WORKSPACE_TABLE_SCROLL_Y }}
      />
    </>
  );

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <GovernanceRiskHeader compact title="💰 Финансовые риски и блокировки" />

        <Card size="small" title="Сводка (по текущим страницам списков)">
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} lg={6}>
              <Statistic
                title="Дебиторская задолженность"
                value={workspaceKpi.receivable}
                formatter={(v) => formatCurrency(Number(v))}
              />
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Statistic
                title="Кредиторская задолженность"
                value={workspaceKpi.payable}
                formatter={(v) => formatCurrency(Number(v))}
              />
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Statistic
                title="Финансовый результат (факт)"
                value={workspaceKpi.profitFact}
                formatter={(v) => formatCurrency(Number(v))}
              />
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Statistic
                title="Финансовый результат (план)"
                value={workspaceKpi.profitPlan}
                formatter={(v) => formatCurrency(Number(v))}
              />
            </Col>
          </Row>
        </Card>

        <Card title="Маршруты (к оплате)">{routesSection}</Card>

        <Card title="Заявки (к получению)">{applicationsSection}</Card>
      </Space>

      <FinanceDetailsDrawer
        open={drawerOpen}
        mode={drawerMode}
        route={selectedRoute}
        applicationId={selectedApplicationId}
        onClose={handleDrawerClose}
        onRefetchRoutes={() => void refetch()}
        onRefetchApplications={() => void refetchApps()}
      />
    </div>
  );
};

export default FinanceWorkspacePage;
